import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useDebounceFn } from '@vueuse/core'
import { useJournalDetailTable } from './detail/useJournalDetailTable'
import { useJournalDetailFilters } from './detail/useJournalDetailFilters'
import { useJournalDetailStore } from '@/views/apps/journals/store/journalDetailStore'
import { useJournalEntriesStore } from '@/views/apps/journals/store/journalEntriesStore'
import { useJournalFilterStore } from '@/views/apps/journals/store/journalFilterStore'
import { DEBOUNCE_DELAY_MS } from '@/views/apps/journals/constants/apiEndpoints'
import {
  formatAttributeValue,
  formatPersonName,
} from '@/views/apps/journals/utils/formatters'
import { useErrorHandler } from '@/views/apps/journals/composables/useErrorHandler'
import { getFileNameFromPath, getFileUrl } from '@/views/apps/journals/utils/fileUtils'

const ENTRY_QUERY_KEY = 'entry'

function parseEntryIdFromQuery(value: unknown): number | null {
  const raw = Array.isArray(value) ? value[0] : value
  if (raw == null || raw === '') {
    return null
  }
  const id = Number(raw)

  return Number.isInteger(id) && id > 0 ? id : null
}

export function useJournalDetailPage() {
  const route = useRoute()
  const router = useRouter()
  const detailStore = useJournalDetailStore()
  const entriesStore = useJournalEntriesStore()
  const journalFilterStore = useJournalFilterStore()
  const { handleError } = useErrorHandler()

  const journalId = computed(() => Number(route.params.id))

  const {
    currentJournal,
    isFetchingJournal,
    isFetchingAttributes,
  } = storeToRefs(detailStore)

  const { isFetchingEntries, entrySearchQuery } = storeToRefs(entriesStore)

  const {
    getSortIcon,
    handleSort,
    itemsPerPage,
    onEntriesOptionsUpdate,
    page,
    processedEntries,
    syncAfterInitialFetch,
    tableHeaders,
    totalEntriesCount,
    visibleAttributes,
  } = useJournalDetailTable({ journalId })

  const {
    activeFilterChips,
    applyEntryFiltersAndFetch,
    hasActiveFilters,
    isFilterActive,
    removeFilterChip,
    resetAllFilters,
  } = useJournalDetailFilters({ journalId })

  const isEntryModalOpen = ref(false)
  const selectedEntryId = ref<number | null>(null)
  let isSyncingEntryUrl = false

  const canCreate = computed(() => currentJournal.value?.can_create === true)
  const journalName = computed(() =>
    isFetchingJournal.value
      ? 'Загрузка...'
      : currentJournal.value?.name || `Журнал #${journalId.value}`,
  )

  const syncEntryQuery = (entryId: number | null) => {
    if (isSyncingEntryUrl) {
      return
    }

    const current = parseEntryIdFromQuery(route.query[ENTRY_QUERY_KEY])
    if (current === entryId) {
      return
    }

    const query = { ...route.query }
    if (entryId != null) {
      query[ENTRY_QUERY_KEY] = String(entryId)
    }
    else {
      delete query[ENTRY_QUERY_KEY]
    }

    void router.replace({ query })
  }

  const loadJournalData = async () => {
    try {
      await detailStore.fetchJournal(journalId.value)
      await entriesStore.fetchJournalEntries(journalId.value)
      syncAfterInitialFetch()
    }
    catch (error) {
      handleError(error, 'Ошибка загрузки данных журнала')
    }
  }

  const createEntry = () => {
    selectedEntryId.value = null
    isEntryModalOpen.value = true
    syncEntryQuery(null)
  }

  const editEntry = (entryId: number) => {
    selectedEntryId.value = entryId
    isEntryModalOpen.value = true
    syncEntryQuery(entryId)
  }

  const onEntryRowClick = (
    _event: unknown,
    payload: { item?: { id: number }; internalItem?: { raw?: { id: number } } },
  ) => {
    const entryId = payload?.item?.id ?? payload?.internalItem?.raw?.id
    if (entryId && typeof entryId === 'number') {
      editEntry(entryId)
    }
  }

  const onEntrySaved = () => {
    isEntryModalOpen.value = false
    selectedEntryId.value = null
    syncEntryQuery(null)
  }

  const entrySort = computed(() => entriesStore.entrySort)

  const getEntryFilter = (attrId: number | string) =>
    entriesStore.entryFilters[String(attrId)]

  const setEntryFilter = (attrId: number | string, value: unknown) =>
    entriesStore.setEntryFilter(String(attrId), value)

  const debouncedEntrySearch = useDebounceFn(() => {
    if (!journalId.value) {
      return
    }

    const queryLength = entrySearchQuery.value.length
    if (queryLength > 0 && queryLength <= 2) {
      return
    }

    entriesStore.fetchJournalEntries(journalId.value)
  }, DEBOUNCE_DELAY_MS)

  watch(entrySearchQuery, () => debouncedEntrySearch())

  watch(
    () => entriesStore.entrySort,
    () => {
      if (journalId.value) {
        entriesStore.setCursor(undefined)
        entriesStore.fetchJournalEntries(journalId.value)
      }
    },
  )

  watch(
    journalId,
    id => {
      journalFilterStore.setJournalId(id && !Number.isNaN(id) ? id : null)
    },
    { immediate: true },
  )

  watch(isEntryModalOpen, open => {
    if (!open) {
      selectedEntryId.value = null
      syncEntryQuery(null)
    }
  })

  watch(
    () => parseEntryIdFromQuery(route.query[ENTRY_QUERY_KEY]),
    entryId => {
      if (entryId == null) {
        if (isEntryModalOpen.value && selectedEntryId.value != null) {
          isSyncingEntryUrl = true
          isEntryModalOpen.value = false
          selectedEntryId.value = null
          isSyncingEntryUrl = false
        }
        return
      }

      if (selectedEntryId.value === entryId && isEntryModalOpen.value) {
        return
      }

      isSyncingEntryUrl = true
      selectedEntryId.value = entryId
      isEntryModalOpen.value = true
      isSyncingEntryUrl = false
    },
  )

  onMounted(async () => {
    await loadJournalData()

    const entryFromUrl = parseEntryIdFromQuery(route.query[ENTRY_QUERY_KEY])
    if (entryFromUrl != null) {
      editEntry(entryFromUrl)
    }
  })

  onUnmounted(() => {
    journalFilterStore.setJournalId(null)
    detailStore.clearJournalData()
    entriesStore.clearEntriesData()
  })

  return {
    activeFilterChips,
    applyEntryFiltersAndFetch,
    canCreate,
    createEntry,
    currentJournal,
    entrySearchQuery,
    entrySort,
    formatAttributeValue,
    formatPersonName,
    getEntryFilter,
    getFileNameFromPath,
    getFileUrl,
    getSortIcon,
    handleSort,
    hasActiveFilters,
    isEntryModalOpen,
    isFetchingAttributes,
    isFetchingEntries,
    isFetchingJournal,
    isFilterActive,
    itemsPerPage,
    journalId,
    journalName,
    onEntriesOptionsUpdate,
    onEntryRowClick,
    onEntrySaved,
    page,
    processedEntries,
    removeFilterChip,
    resetAllFilters,
    selectedEntryId,
    setEntryFilter,
    tableHeaders,
    totalEntriesCount,
    visibleAttributes,
  }
}
